import React from "react";
import {InputAdornment, MenuItem} from "@mui/material";
import {useMessages} from "i18n";
import BaseInput from "components/BaseInput";
import {BaseSelect} from "components/BaseSelect";
import {CardSheet} from "components/CardSheet";
import {CreateFormVM} from "./useCreateForm";
import {ProPill} from "./UpgradeHints";
import SuperTooltip from "../../components/SuperTooltip";
import {EmailField} from "./FormField";
import {InfoIcon} from "./icons";
import type {PaidPlanId} from "pricing/planCatalogue";
import {RecipientsSection} from "./RecipientsSection";
import {
	Column, expiryMenuProps, ExpiryOption, FieldHint, FooterAction, FooterActions, FooterInfo,
	FooterLimit, FooterPlan, FooterRoot, HintButton, OptionBlock, OptionDesc, OptionHeader, OptionMeta,
	OptionName, OptionReveal, Section, SectionLabel, Switch,
	UpgradeFooterAction,
} from "./OptionsSheet.styles";

interface Props {
	/** Whether the sheet is open — owned by {@link TransferPanel}. */
	open: boolean;
	/** Close the non-modal sheet (Escape / ✕). */
	onClose: () => void;
	/** Release TransferPanel's single editor owner after the exit transition. */
	onExited: () => void;
	/** Whether an ordinary close should return focus to the button that opened this editor. */
	restoreFocusOnClose: boolean;
	/** The opener captured before TransferPanel makes its sender controls inert. */
	returnFocus: HTMLElement | null;
	/** The create-form view-model — the whole contract (epic #26). */
	vm: CreateFormVM;
	/**
	 * Open the 3-plan dialog (footer Upgrade button). Owned by {@link TransferPanel}, which mounts the
	 * dialog as a SIBLING of this sheet so it outlives the sheet's dismissal (ui#217) — it must never
	 * be routed anywhere: navigating away destroys the in-progress transfer.
	 *
	 * The footer keeps the whole ladder on the owner's word (2026-09-17, ui#2528): it is the one
	 * control in this sheet that names no tier, so there is nothing for a single-plan pitch to sell.
	 */
	onUpgrade: () => void;
	/**
	 * Opens an upgrade surface on ONE tier (ui#2524). The filled-list nudge sells a specific plan, so
	 * handing it `onUpgrade` would drop the tier it named and show the whole ladder instead.
	 *
	 * WHICH surface that is belongs to {@link TransferPanel} and its `upgradeDialogFor` rule, never to
	 * this sheet: since ui#2529 the recipients nudge opens the compact single-plan dialog while the
	 * footer above still opens the table, and a view that decided that itself would be a second place
	 * the owner's list has to be kept in step.
	 */
	onUpgradeTo: (plan: PaidPlanId) => void;
	/**
	 * That tier's monthly price, already formatted in the reader's locale and the storefront's
	 * currency. Resolved by {@link TransferPanel}, which owns the storefront quote: on a geographic
	 * storefront the figure this nudge quotes must be the one the dialog it opens will charge.
	 */
	monthlyPriceOf: (plan: PaidPlanId) => string;
}

/**
 * The body of the transfer-options CardSheet (ui#214/ui#1061). The non-modal dialog keeps navigation
 * visible, accessible and clickable while consolidating every option that used to live in the panel's
 * inline reveal rows + bottom `Actionbar` popover:
 *  - **Your email** — the signed-in sender identity, relocated off the panel (ui#853). Read-only
 *    because the authenticated account email is canonical (`canonicalSenderEmail`) — it says who the
 *    transfer is from, it is not a send-on-behalf field. Signed out, the row stays on the panel where
 *    the mandatory address is typed.
 *  - **Email to people** — the unified recipient chip field (email chips + group chips + contacts
 *    picker, ui#210–#213), always visible. The [Link only | Email to people] tabs that used to gate it
 *    are gone (ui#890): the link is produced either way, so the tabs offered a choice that did not
 *    exist. Recipients are purely additive — leave the field empty and you get the link; fill it and
 *    those people are emailed the same link as well (`deliveryOf` in `./createFormLogic` derives
 *    which of the two sends that is, from the chips themselves).
 *  - **Message** — a POINTER into the personalization builder, and nothing else. The Title + Message
 *    fields lived here from ui#216 until the owner moved them into the builder on 2026-09-01 (ui#1814),
 *    where the sender writes them while watching the page their recipient will open. The sheet keeps no
 *    copy of them: one piece of state, edited in one place.
 *  - **Password** / **Download limit** / **Zero-Knowledge** / **Download alerts** / **Recover after
 *    expiry** — labeled switch rows; password + download limit reveal their input when armed. Every
 *    row is always visible and gated per option via `proPill`/`toggle` (the old E2EE rollout flag was
 *    retired in ui#306). Download alerts are the api#92 per-transfer opt-in (Basic+); recovery is the
 *    api#443 per-transfer opt-in (Premium). Zero-Knowledge and recovery each carry an info glyph whose
 *    hint is revealed on hover and on keyboard focus (api#443).
 *  - **Coming soon** — custom link / scheduled send (ui#216): inert, screen-reader
 *    disabled rows for Premium options with no backend yet. They carry no state and never reach
 *    `buildPayload`.
 *  - **Expiry** — FIRST in the sheet since 2026-08-31 (owner decision; it had sat last after moving in
 *    from the `Actionbar`); only shown for hosted uploads (instant/P2P has no TTL). A single {@link ../../components/BaseSelect} picker since ui#1201: the ladder reached fifteen
 *    rungs with the sub-day steps (kitecorp/transferify#90) and the chip grid it used to be dominated
 *    the sheet. Every rung stays reachable and every tier-locked one stays VISIBLE with its pill —
 *    picking one still routes to the upgrade dialog rather than applying, exactly as the chips did.
 *  - **Footer** — current plan + hosted-file size limit, with an optional Upgrade action followed by
 *    Save. Save only dismisses the sheet because every option already updates live.
 *
 * A dumb view: every value + handler comes from the view-model; colours come from `theme.tokens`.
 */
export function OptionsSheet({
	open, onClose, onExited, restoreFocusOnClose, returnFocus, vm, onUpgrade, onUpgradeTo, monthlyPriceOf,
}: Props) {
	const {sender, pricing, common} = useMessages();
	const sheet = sender.optionsSheet;
	const passwordHintId = React.useId();
	const optionDescriptionId = React.useId();
	const footer = (
		<FooterRoot>
			<FooterInfo>
				<FooterPlan>{pricing.planNames[vm.plan]}</FooterPlan>
				<FooterLimit>{sheet.hostedLimit(vm.hostedLimitLabel)}</FooterLimit>
			</FooterInfo>
			<FooterActions data-testid="options-actions">
				{vm.recipientCanUpgrade && (
					<UpgradeFooterAction type="button" color="primary" variant="contained" onClick={onUpgrade}>
						{common.upgrade}
					</UpgradeFooterAction>
				)}
				<FooterAction type="button" color="primary" variant="contained" onClick={onClose}>
					{common.save}
				</FooterAction>
			</FooterActions>
		</FooterRoot>
	);

	return (
		<CardSheet
			open={open}
			onClose={onClose}
			onExited={onExited}
			restoreFocusOnClose={restoreFocusOnClose}
			returnFocus={returnFocus}
			title={sheet.title}
			titleAccent={sheet.titleAccent}
			subtitle={sheet.subtitle}
			closeLabel={common.close}
			footer={footer}
			role="dialog"
		>
			<Column>
				{/* Expiry — FIRST in the dialog (owner, 2026-08-31: the retention is the setting most senders
				  * open this sheet for). Hosted uploads only; instant/P2P links have no TTL (ui#128). */}
				{vm.hostedUpload && (
					<Section>
						<SectionLabel>{sender.expiryPopLabel}</SectionLabel>
						<BaseSelect
							/* Stable and locale-independent, for the same reason FeedbackTopicSelect passes one:
							 * BaseSelect would otherwise generate an id per render tree, and this control is worth
							 * recognising by name in a DOM dump. */
							id="transfer-expiry"
							label={sheet.expiryFieldLabel}
							/* No `shrink` here, unlike FeedbackTopicSelect: that one needs it because `displayEmpty`
							 * draws a placeholder at `value === ""`, which MUI reads as NOT filled. This picker is
							 * never empty — `coerceExpiry` keeps a plan-legal rung selected at all times — so MUI
							 * floats the label from the value on its own, including for `ONE_DAY`, whose ordinal is 0. */
							value={vm.expireAfter}
							/* The closed field says the retention and nothing else, from the VM's own single-sourced
							 * label. Rendering the selected row's children instead would drag its layout (and, the
							 * day a locked rung could be current, its upsell pill) into the field. */
							renderValue={() => vm.expiryLabel}
							MenuProps={expiryMenuProps}
							onChange={(event: {target: {value: unknown}}) => {
								const chosen = vm.expiryChoices.find(choice => choice.value === Number(event.target.value));
								/* `select()` carries the WHOLE rule (see useCreateForm): an entitled rung sets the
								 * retention, a locked one routes to the upgrade dialog and leaves the retention alone.
								 * That is what keeps a locked rung selectable-to-upsell — visible and reachable —
								 * rather than either hidden or silently applied. This view decides nothing. */
								chosen?.select();
							}}
						>
							{vm.expiryChoices.map(choice => (
								/* Locked rungs stay ENABLED on purpose: `disabled`/`aria-disabled` would drop them out
								 * of the roving focus order AND out of MUI's closed-field typeahead, taking the upsell
								 * with them on exactly the tiers it exists for. What carries the gate is the pill's own
								 * label — named on the PILL rather than on the row (the `PersonaliseSheet` idiom), so
								 * the browser still composes the row's name from its contents and the rung's own label
								 * is never restated. An `aria-label` here would REPLACE that name instead.
								 * Badge and gloss both come from `choice.locked`, which the view-model resolved from
								 * the rung's OWN unlock tier: the day rungs sell BASIC or PLUS, the indefinite one
								 * sells PREMIUM, and a fixed string here sold the wrong tier on most of them
								 * (ui#1203). */
								<MenuItem key={choice.value} value={choice.value}>
									<ExpiryOption>
										{choice.label}
										{choice.locked !== null && (
											<ProPill aria-label={choice.locked.aria}>
												{choice.locked.pillLabel}
											</ProPill>
										)}
									</ExpiryOption>
								</MenuItem>
							))}
						</BaseSelect>
					</Section>
				)}

				{/* There is no Message section here at all. Title, message and sender name moved into the
				  * personalization builder (ui#1814, owner 2026-09-01) and this sheet kept a POINTER line
				  * saying where they went; the owner removed that too on 2026-09-03, once the builder had its
				  * own button on the progress card. A signpost is worth its space only while people are still
				  * looking in the old place, and it was costing a titled section in the middle of the sheet.
				  * What has not changed is the rule behind the move: a both-doors form was considered and
				  * refused, because title, message and sender name are ONE piece of state wherever they are
				  * edited, never two that can diverge. Do not reintroduce either the fields or the pointer. */}

				{/* Your email — the signed-in sender identity row (ui#853); see the component doc above. */}
				{vm.senderEmailLocked && (
					<Section>
						<SectionLabel>{sender.senderEmail}</SectionLabel>
						<EmailField
							value={vm.senderEmail}
							onChange={vm.setSenderEmail}
							placeholder={sender.senderEmail}
							tip={sender.senderEmailTip}
							infoIcon={<InfoIcon/>}
							readOnly
						/>
					</Section>
				)}

				{/* Email to people — the unified recipient field, always shown (ui#890); see the doc above. */}
				<RecipientsSection vm={vm} label={sender.emailToPeople} onUpgradeTo={onUpgradeTo} monthlyPriceOf={monthlyPriceOf}/>

				{/* Option toggles — every row always rendered; entitlement shows as a pill, not a hidden row. */}
				{vm.options.map(option => (
					<OptionBlock key={option.key}>
						<OptionHeader>
							<OptionMeta>
								<OptionName>
									{option.label}
									{option.proPill && <ProPill>{option.pillLabel}</ProPill>}
									{option.hint !== undefined && (
										/* describeChild keeps the hint an aria-DESCRIPTION: without it MUI would
										 * make the copy the glyph's accessible NAME and shadow hintAria, so a
										 * screen reader would announce a paragraph where a label belongs.
										 * MUI's Tooltip binds hover AND focus on the child, which is what makes
										 * the hint reachable by keyboard (api#443). */
										<SuperTooltip title={option.hint} describeChild>
											<HintButton type="button" aria-label={sheet.hintAria(option.label)}>
												i
											</HintButton>
										</SuperTooltip>
									)}
								</OptionName>
								<OptionDesc id={`${optionDescriptionId}-${option.key}`} aria-live="polite">{option.desc}</OptionDesc>
							</OptionMeta>
							<Switch
								type="button"
								role="switch"
								$on={option.on}
								aria-checked={option.on}
								aria-label={option.label}
								aria-describedby={`${optionDescriptionId}-${option.key}`}
								disabled={option.disabled}
								onClick={option.toggle}
							>
								<span/>
							</Switch>
						</OptionHeader>
						{option.on && option.key === "password" && (
							<OptionReveal>
								<BaseInput
									type="password"
									label={sheet.passwordToggleAria}
									value={vm.password}
									error={vm.passwordHint.error}
									/* The helper line is the field's DESCRIPTION (ui#384), so a screen reader hears
									 * the bounds — or the refusal — with the field rather than as loose text after it. */
									aria-describedby={passwordHintId}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => vm.setPassword(e.target.value)}
								/>
								<FieldHint
									id={passwordHintId}
									$error={vm.passwordHint.error}
									/* A refusal is announced as it appears; the plain bounds are not worth interrupting for. */
									role={vm.passwordHint.error ? "alert" : undefined}
								>
									{vm.passwordHint.text}
								</FieldHint>
							</OptionReveal>
						)}
						{option.on && option.key === "dlimit" && (
							<OptionReveal>
								<BaseInput
									type="number"
									label={sheet.downloadLimitToggleAria}
									value={vm.dlimit}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => vm.setDlimit(e.target.value)}
									inputProps={{min: 1, max: 100}}
									endAdornment={<InputAdornment position="end">{sender.downloadsMaxUnit}</InputAdornment>}
								/>
							</OptionReveal>
						)}
					</OptionBlock>
				))}
			</Column>
		</CardSheet>
	);
}
